import _ from 'underscore';

import {AlignmentComment} from 'analyses/alignment/AlignmentComments';
import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {Part} from 'biojs/Part';
import {Casing} from 'biojs/Sequence';
import {ComponentsSequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {Strand} from 'constants/MolbioConstants';
import Annotation from 'molbio/annotations/backbone/Annotation';
import CutSite from 'molbio/enzymes/backbone/CutSite';
import {PolymerMapState} from 'molbio/maps/MapStates';
import PolymerMapTabView from 'molbio/maps/PolymerMapTabView';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import PolymerMapTabState from 'molbio/tabstates/PolymerMapTabState';
import ORF from 'molbio/translations/backbone/ORF';
import Translation from 'molbio/translations/backbone/Translation';
import {TabState} from 'Navigation';

export interface MenuChoicesOptions {
  sequence?: ComponentsSequence;
  translation?: Translation;
  annotation?: Annotation;
  mapstate?: PolymerMapState;
  tabstate?: TabState;
  comment?: AlignmentComment;
  part?: Part;
  primer?: SequencePrimer;
  cutsite?: CutSite;
  orf?: ORF;
  feature?: Annotation | Translation | Part | SequencePrimer | ORF;
  model?: PolymerMapState;
  circular?: boolean;
  alignment?: boolean;
  hasTrim?: boolean;
  polymerMapTabView?: PolymerMapTabView<PolymerMapTabState<ComponentsSequence>>;
}

export function hasAlignmentModel(options: MenuChoicesOptions): boolean {
  return Boolean(options.model?.has('alignment'));
}

export function trackChoice(action: string, moreMetadata: {name?: string; strand?: Strand} = {}): void {
  // eslint-disable-next-line no-restricted-syntax
  analytics.track('editor', action, _.extend({source: 'contextmenu'}, moreMetadata));
}

export function trackCreatePrimer(sequence: ComponentsSequence, direction: string): void {
  analytics.track('primer', 'open_edit_primer_tab', {
    product: ANALYTICS_MODULE.MOLBIO,
    createdFrom: 'context_menu',
    direction,
    sequence_id: sequence.get('id'),
  });
}

export function trackOpenAnnotationPanel(sequence: ComponentsSequence, openedFrom: string): void {
  analytics.track('annotation', 'open_annotation_panel', {
    product: ANALYTICS_MODULE.MOLBIO,
    openedFrom,
    sequence_id: sequence.get('id'),
  });
}

export function trackOpenTranslationPanel(sequence: ComponentsSequence, strand: Strand | null): void {
  analytics.track('translation', 'open_translation_panel', {
    product: ANALYTICS_MODULE.MOLBIO,
    openedFrom: 'context_menu',
    strand,
    sequence_id: sequence.get('id'),
  });
}

export function translateCodon(sequence: ComponentsSequence, translation: Translation): string {
  let bases;
  const [start, end] = sequence.get('selection').getEndpoints();
  assert(end != null, 'Expected selection end to be non-null when translating codon.');
  if (translation.strand() === 1) {
    ({bases} = sequence.get('dna').forwardSlice(start, end));
  } else {
    bases = sequence.get('dna').reverseSlice(start, end).bases.split('').reverse().join('');
  }
  return bases.toUpperCase();
}

export function canOpenPartAtSelectionStart(
  sequence: ComponentsSequence | GapSequenceWrapper,
  overlappingPart?: Part | null
): boolean {
  if (overlappingPart != null) {
    return !overlappingPart.isDeleted();
  } else {
    const parts = sequence.getSequencePartsAtSelectionStart();
    return parts.length === 1 && parts[0] != null && !parts[0].isDeleted();
  }
}

export function canUnlinkPartAtSelectionStart(
  sequence: ComponentsSequence | GapSequenceWrapper,
  overlappingPart: Part | null
): boolean {
  const canUnlink =
    BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS || BenchlingConfig.PARTS_ENABLE_CROSS_ORIGIN_PARTS
      ? sequence.allowFeatureEdits()
      : sequence.allowResidueEdits();
  return canOpenPartAtSelectionStart(sequence, overlappingPart) && canUnlink;
}

export function canDeleteOrReplacePartAtSelectionStart(
  sequence: ComponentsSequence | GapSequenceWrapper
): boolean {
  const parts = sequence.getSequencePartsAtSelectionStart();
  return (
    canOpenPartAtSelectionStart(sequence) &&
    sequence.allowResidueEdits() &&
    !sequence.partHasOverlaps(parts[0])
  );
}

export function canEditPartAtSelectionStart(sequence: ComponentsSequence | GapSequenceWrapper): boolean {
  const parts = sequence.getSequencePartsAtSelectionStart();
  return (
    canOpenPartAtSelectionStart(sequence) &&
    sequence.allowResidueEdits() &&
    parts[0].isInSync(sequence._sequence()) &&
    !sequence.partHasOverlaps(parts[0])
  );
}

export function shouldShowPartMenu(
  sequence: ComponentsSequence | GapSequenceWrapper,
  overlappingPart?: Part | null
): boolean {
  return (
    (overlappingPart != null || sequence.getSequencePartsInSelection().length === 1) &&
    sequence.selectionContainedInPart() &&
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT})
  );
}

export function shouldShowMultiPartMenu(sequence: ComponentsSequence, part: Part): boolean {
  const parts = sequence.getSequencePartsInSelection();
  return parts.length > 1 && sequence.selectionContainedInPart() && parts.includes(part);
}

export function getSinglePart(sequence: ComponentsSequence | GapSequenceWrapper): Part {
  const parts = sequence.getSequencePartsAtSelectionStart();
  assert(parts.length === 1, 'Expected to find exactly one part');
  return parts[0];
}

export function toggleCaseHandler(sequence: ComponentsSequence, casing: Casing): void {
  const selection = sequence.get('selection').assertNotEmpty();
  const start = selection.start();
  const end = selection.end();
  sequence.toggleCase(start, end, casing);
}

export function getTranslationMenuActionName(prefix: string, translation: Translation | undefined): string {
  const numRegions = translation?.groupTranslations().length ?? 1;
  return numRegions > 1 ? `${prefix} all translation regions` : `${prefix} translation`;
}
